import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  MenuItem,
  Button,
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  Stack,
  IconButton,
} from '@mui/material';
import useApiService from '../../component/hooks/useApiService';
import { useAppSnackbar } from '../../component/providers/SnackbarProvider';
import { useDialog } from '../../context/dialog/DialogContext';
import iconLibrary from '../../constans/icons';
import DataGrid from '../../component/ui/data-grid/DataGrid';

const STATUS_OPTIONS = [
  { value: '', label: 'Tümü' },
  { value: 'pending', label: 'Beklemede' },
  { value: 'in_review', label: 'İncelemede' },
  { value: 'approved', label: 'Onaylandı' },
  { value: 'rejected', label: 'Reddedildi' },
];

const statusColor = (status) => {
  switch (status) {
    case 'approved':
      return 'success';
    case 'in_review':
      return 'info';
    case 'pending':
      return 'default';
    case 'rejected':
      return 'error';
    default:
      return 'default';
  }
};

const formatMoney = (val) => {
  if (val === null || val === undefined || Number.isNaN(val)) return '-';
  return `$${Number(val).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

const FriendReferrals = () => {
  const { executeGet, executePost, executePatch, executeDelete, extractApiError } = useApiService();
  const { showError, showSuccess } = useAppSnackbar();
  const { openDialog } = useDialog();

  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ total: 0, limit: 25, offset: 0 });
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
  const [filters, setFilters] = useState({ status: '', user_email: '', friend_email: '' });

  const [createForm, setCreateForm] = useState({
    user_email: '',
    friend_name: '',
    friend_email: '',
    friend_phone: '',
    note: '',
    status: 'pending',
  });

  const params = useMemo(() => {
    const p = {};
    Object.entries(filters).forEach(([k, v]) => {
      if (v) p[k] = v;
    });
    p.limit = paginationModel.pageSize;
    p.offset = paginationModel.page * paginationModel.pageSize;
    return p;
  }, [filters, paginationModel]);
  const paramsRef = useRef(params);
  const parseApiMessage = (err, fallback) => err?.response?.data?.message || fallback;

  const executeGetRef = useRef(executeGet);
  const showErrorRef = useRef(showError);
  const isMountedRef = useRef(true);
  const loadRef = useRef(null);
  const hasLoadedRef = useRef(false);
  useEffect(() => { executeGetRef.current = executeGet; }, [executeGet]);
  useEffect(() => { showErrorRef.current = showError; }, [showError]);
  useEffect(() => () => { isMountedRef.current = false; }, []);
  useEffect(() => { paramsRef.current = params; }, [params]);

  useEffect(() => {
    loadRef.current = async () => {
      setLoading(true);
      const currentParams = paramsRef.current;
      try {
        const res = await executeGetRef.current({ url: '/api/admin/referrals', params: currentParams });
        if (!isMountedRef.current) return;

        const items = res?.items || res || [];
        const metaRes = res?.meta || { total: items.length, limit: currentParams.limit || items.length, offset: 0 };
        setRows(items);
        setMeta(metaRes);
      } catch (e) {
        console.error('Referral list fetch failed', e);
        showErrorRef.current?.('Referans kayıtları yüklenemedi.');
        setRows([]);
      } finally {
        if (isMountedRef.current) setLoading(false);
      }
    };
  }, []);

  useEffect(() => {
    if (hasLoadedRef.current) {
      loadRef.current?.();
      return;
    }
    hasLoadedRef.current = true;
    loadRef.current?.();
  }, [paginationModel]);

  const onFilterChange = (field) => (e) => {
    const value = e.target.value;
    setFilters((prev) => ({ ...prev, [field]: value }));
  };
  const applyFilters = () => {
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    loadRef.current?.();
  };

  const parseNumber = (value) => {
    if (value === null || value === undefined || value === '') return null;
    const num = Number(value);
    return Number.isNaN(num) ? null : num;
  };

  const saveEdit = async (referral, formValues, close) => {
    if (!referral) return;
    const payload = {};

    if (formValues.status && formValues.status !== referral.status) payload.status = formValues.status;
    const inv = parseNumber(formValues.investment_amount);
    if (inv !== null) payload.investment_amount = inv;

    const lot = parseNumber(formValues.lot_volume);
    if (lot !== null) payload.lot_volume = lot;

    if (formValues.admin_note !== undefined && formValues.admin_note !== referral.admin_note) {
      payload.admin_note = formValues.admin_note;
    }

    const userBonus = parseNumber(formValues.user_bonus_amount);
    if (userBonus !== null) payload.user_bonus_amount = userBonus;
    const friendBonus = parseNumber(formValues.friend_bonus_amount);
    if (friendBonus !== null) payload.friend_bonus_amount = friendBonus;

    if (Object.keys(payload).length === 0) {
      showError?.('Lütfen en az bir alanı değiştirin.');
      return;
    }

    try {
      await executePatch({ url: `/api/admin/referrals/${referral.id}`, data: payload });
      showSuccess?.('Kayıt güncellendi.');
      close?.(true);
      loadRef.current?.();
    } catch (e) {
      console.error('Referral update failed', e);
      showError?.(parseApiMessage(e, extractApiError(e, 'Güncelleme başarısız.')));
    }
  };

  const createReferral = async () => {
    const email = (createForm.user_email || '').trim();
    if (!email) {
      showError?.('Kullanıcı e-posta gereklidir.');
      return;
    }
    if (!createForm.friend_name.trim()) {
      showError?.('Referans adı zorunludur.');
      return;
    }

    const payload = {
      user_email: email,
      friend_name: createForm.friend_name,
      friend_email: createForm.friend_email || undefined,
      friend_phone: createForm.friend_phone || undefined,
      note: createForm.note || undefined,
      status: createForm.status || undefined,
    };

    try {
      await executePost({ url: '/api/admin/referrals', data: payload });
      showSuccess?.('Kayıt oluşturuldu.');
      setCreateForm({
        user_email: '',
        friend_name: '',
        friend_email: '',
        friend_phone: '',
        note: '',
        status: 'pending',
      });
      loadRef.current?.();
    } catch (e) {
      console.error('Referral create failed', e);
      showError?.(parseApiMessage(e, extractApiError(e, 'Oluşturma başarısız.')));
    }
  };

  const resetCreateForm = () => {
    setCreateForm({
      user_email: '',
      friend_name: '',
      friend_email: '',
      friend_phone: '',
      note: '',
      status: 'pending',
    });
  };

  const openEditDialog = (referral) => {
    const EditReferralDialog = ({ data, close }) => {
      const [form, setForm] = useState({
        status: data.status || '',
        investment_amount: data.investment_amount ?? '',
        lot_volume: data.lot_volume ?? '',
        admin_note: data.admin_note ?? '',
        user_bonus_amount: data.projected_user_bonus ?? '',
        friend_bonus_amount: data.projected_friend_bonus ?? '',
      });
      const [saving, setSaving] = useState(false);

      const setField = (key) => (e) => setForm((prev) => ({ ...prev, [key]: e.target.value }));

      const handleSave = async () => {
        setSaving(true);
        try {
          await saveEdit(data, form, close);
        } finally {
          setSaving(false);
        }
      };

      return (
        <Box sx={{ width: 480, maxWidth: '100%' }}>
          <Stack spacing={1.5}>
            <TextField
              select
              size="small"
              label="Durum"
              value={form.status}
              onChange={setField('status')}
              fullWidth
            >
              {STATUS_OPTIONS.filter((s) => s.value !== '').map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              label="Yatırım tutarı (USD)"
              value={form.investment_amount}
              onChange={setField('investment_amount')}
              fullWidth
            />
            <TextField
              size="small"
              label="Lot hacmi"
              value={form.lot_volume}
              onChange={setField('lot_volume')}
              fullWidth
            />
            <TextField
              size="small"
              label="Admin notu"
              value={form.admin_note}
              onChange={setField('admin_note')}
              fullWidth
              multiline
              minRows={2}
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <TextField
                size="small"
                label="Kullanıcı bonusu (manuel)"
                value={form.user_bonus_amount}
                onChange={setField('user_bonus_amount')}
                fullWidth
              />
              <TextField
                size="small"
                label="Referans bonusu (manuel)"
                value={form.friend_bonus_amount}
                onChange={setField('friend_bonus_amount')}
                fullWidth
              />
            </Stack>
            <Stack direction="row" justifyContent="flex-end" spacing={1}>
              <Button variant="text" onClick={() => close?.(false)} disabled={saving}>Vazgeç</Button>
              <Button variant="contained" onClick={handleSave} disabled={saving}>
                {saving ? 'Kaydediliyor...' : 'Değişiklikleri kaydet'}
              </Button>
            </Stack>
          </Stack>
        </Box>
      );
    };

    openDialog({
      title: `Referansı düzenle #${referral.id}`,
      maxWidth: 'sm',
      content: <EditReferralDialog data={referral} />,
    });
  };

  const confirmDelete = (referral) => {
    const ConfirmDeleteDialog = ({ close }) => (
      <Box>
        <Typography gutterBottom>
          Bu referans kaydı kalıcı olarak silinecek. Emin misiniz?
        </Typography>
        <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ mt: 2 }}>
          <Button variant="text" onClick={() => close?.(false)}>Vazgeç</Button>
          <Button
            variant="contained"
            color="error"
            onClick={async () => {
              try {
                await executeDelete({ url: `/api/admin/referrals/${referral.id}` });
                showSuccess?.('Kayıt silindi.');
                loadRef.current?.();
                close?.(true);
              } catch (e) {
                console.error('Referral delete failed', e);
                showError?.(parseApiMessage(e, 'Silme işlemi başarısız.'));
              }
            }}
          >
            Sil
          </Button>
        </Stack>
      </Box>
    );

    openDialog({
      title: `Silinsin mi? (${referral.user_email || 'kullanıcı'})`,
      maxWidth: 'xs',
      content: <ConfirmDeleteDialog />,
    });
  };

  return (
    <Box sx={{ width: '100%', maxWidth: '1200px', mx: 'auto', px: { xs: 1, md: 0 } }}>
      <Typography variant="h4" fontWeight={700} gutterBottom>Referanslar</Typography>

      <Paper sx={{ p: { xs: 1.5, md: 2.5 }, mb: 3 }} elevation={1}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <TextField
              select
              fullWidth
              size="small"
              label="Durum"
              value={filters.status}
              onChange={onFilterChange('status')}
              InputLabelProps={{ shrink: true }}
              SelectProps={{
                displayEmpty: true,
                renderValue: (val) => {
                  if (!val) return 'Tümü';
                  const opt = STATUS_OPTIONS.find((o) => o.value === val);
                  return opt?.label || val;
                },
              }}
            >
              {STATUS_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Kullanıcı e-posta"
              value={filters.user_email}
              onChange={onFilterChange('user_email')}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Referans e-posta"
              value={filters.friend_email}
              onChange={onFilterChange('friend_email')}
            />
          </Grid>
          <Grid item xs={12} md={3} display="flex" alignItems="center" justifyContent={{ xs: 'stretch', md: 'flex-end' }}>
            <Button
              fullWidth
              variant="contained"
              size="medium"
              onClick={applyFilters}
              disabled={loading}
            >
              Filtreleri uygula
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: { xs: 1.5, md: 2.5 }, mb: 3 }} elevation={1}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <Typography variant="h6" fontWeight={700}>Kayıtlar</Typography>
          <Typography variant="body2" color="text.secondary">
            Toplam: {meta.total}
          </Typography>
        </Stack>
        <DataGrid
          rows={rows}
          rowCount={meta.total}
          getRowId={(row) => row.id}
          columns={[
            {
              field: 'user_email',
              headerName: 'Kullanıcı e-posta',
              flex: 1.2,
              minWidth: 180,
              renderCell: (params) => params?.row?.user_email || '—',
            },
            {
              field: 'friend_name',
              headerName: 'Referans',
              flex: 1.2,
              minWidth: 200,
              renderCell: (params) => (
                <Box>
                  <Typography variant="body2" fontWeight={600}>{params?.row?.friend_name}</Typography>
                  <Typography variant="caption" color="text.secondary">{params?.row?.friend_email || '—'}</Typography>
                </Box>
              ),
            },
            {
              field: 'status',
              headerName: 'Durum',
              flex: 0.8,
              minWidth: 120,
              renderCell: (params) => (
                <Chip size="small" label={params?.row?.status || '—'} color={statusColor(params?.row?.status)} />
              ),
            },
            {
              field: 'investment_amount',
              headerName: 'Yatırım',
              type: 'number',
              flex: 0.7,
              minWidth: 120,
              renderCell: (params) => formatMoney(params?.row?.investment_amount),
            },
            {
              field: 'projected_user_bonus',
              headerName: 'Kullanıcı payı',
              type: 'number',
              flex: 0.7,
              minWidth: 140,
              renderCell: (params) => formatMoney(params?.row?.projected_user_bonus),
            },
            {
              field: 'projected_friend_bonus',
              headerName: 'Referans payı',
              type: 'number',
              flex: 0.7,
              minWidth: 140,
              renderCell: (params) => formatMoney(params?.row?.projected_friend_bonus),
            },
            {
              field: 'created_at',
              headerName: 'Oluşturulma',
              flex: 0.8,
              minWidth: 140,
              renderCell: (params) => {
                const value = params?.row?.created_at;
                return value ? new Date(value).toLocaleDateString() : '-';
              },
            },
          ]}
          rowActions={[
            { name: 'edit', icon: 'edit', tooltip: 'Düzenle', onClick: (row) => row && openEditDialog(row) },
            { name: 'delete', icon: 'delete', tooltip: 'Sil', onClick: (row) => row && confirmDelete(row) },
          ]}
          loading={loading}
          paginationModel={paginationModel}
          onPaginationModelChange={(model) => setPaginationModel(model)}
          pageSizeOptions={[10, 25, 50, 100]}
          containerStyle={{ minHeight: 360 }}
          sx={{ '& .MuiDataGrid-cell': { alignItems: 'center' } }}
        />
      </Paper>

      <Grid container spacing={2} justifyContent="center">
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 2 }} elevation={1}>
            <Stack spacing={2}>
              <Box>
                <Typography variant="h6" fontWeight={700}>Yeni kayıt oluştur</Typography>
                <Typography variant="body2" color="text.secondary">
                  Kullanıcı bilgilerini ve davet detayını girin, ardından kaydedin.
                </Typography>
              </Box>
              <Table
                size="small"
                sx={{
                  '& td': { borderBottom: 'none', py: 1 },
                  '& .label': { width: { xs: '35%', sm: 180 }, fontWeight: 600, color: 'text.secondary' },
                }}
              >
                <TableBody>
                  <TableRow>
                  <TableCell className="label">Kullanıcı e-posta</TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      label="E-posta"
                      value={createForm.user_email}
                      onChange={(e) => setCreateForm((prev) => ({ ...prev, user_email: e.target.value }))}
                      fullWidth
                      required
                    />
                  </TableCell>
                </TableRow>
                  <TableRow>
                    <TableCell className="label">Durum</TableCell>
                    <TableCell>
                      <TextField
                        select
                        size="small"
                        value={createForm.status}
                        onChange={(e) => setCreateForm((prev) => ({ ...prev, status: e.target.value }))}
                        fullWidth
                      >
                        {STATUS_OPTIONS.filter((s) => s.value !== '').map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                        ))}
                      </TextField>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="label">Referans adı</TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        value={createForm.friend_name}
                        onChange={(e) => setCreateForm((prev) => ({ ...prev, friend_name: e.target.value }))}
                        fullWidth
                        required
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="label">Referans e-posta</TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        value={createForm.friend_email}
                        onChange={(e) => setCreateForm((prev) => ({ ...prev, friend_email: e.target.value }))}
                        fullWidth
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="label">Referans telefon</TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        value={createForm.friend_phone}
                        onChange={(e) => setCreateForm((prev) => ({ ...prev, friend_phone: e.target.value }))}
                        fullWidth
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="label">Not</TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        value={createForm.note}
                        onChange={(e) => setCreateForm((prev) => ({ ...prev, note: e.target.value }))}
                        fullWidth
                        multiline
                        minRows={2}
                      />
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
              <Stack direction="row" justifyContent="flex-end" spacing={1}>
                <Button variant="outlined" onClick={resetCreateForm} disabled={loading}>Temizle</Button>
                <Button
                  variant="contained"
                  onClick={createReferral}
                  disabled={loading}
                >
                  Kaydı oluştur
                </Button>
              </Stack>
            </Stack>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default FriendReferrals;
